import {handleError, toastInfo} from "helpers"
import {useContext, useState, useRef, useEffect, useCallback} from "react"
import styles from "./VoiceCallIVR.module.css"
import {VoiceCallContext} from "context/VoiceCallContext"

type Props = {
    isVisible: boolean
}

export function VoiceCallIVR(props: Props) {
    const {isVisible} = props
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [digits, setDigits] = useState("")
    const {activeCall} = useContext(VoiceCallContext)
    const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)
    const isLongPressRef = useRef(false)
    const autoSubmitTimerRef = useRef<NodeJS.Timeout | null>(null)
    const AUTO_SUBMIT_DELAY = 1500 // 1.5 seconds after user stops typing

    useEffect(() => {
        if (isVisible) {
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current)
                longPressTimerRef.current = null
            }
            if (autoSubmitTimerRef.current) {
                clearTimeout(autoSubmitTimerRef.current)
                autoSubmitTimerRef.current = null
            }
            isLongPressRef.current = false
            setDigits("")
        }
    }, [isVisible])

    const sendDigits = useCallback(
        async (digitsToSend: string) => {
            if (!activeCall || !digitsToSend.trim() || isSubmitting) {
                return
            }

            try {
                setIsSubmitting(true)
                const digitsWithDelay = digitsToSend.split("").join("w")
                const call: any = activeCall
                if (typeof call.sendDigits === "function") {
                    call.sendDigits(digitsWithDelay)
                } else if (typeof call.dtmf === "function") {
                    for (const digit of digitsToSend) {
                        call.dtmf(digit)
                    }
                } else {
                    throw new Error("DTMF is not supported on this call")
                }
                setDigits("")
            } catch (e) {
                handleError(e)
            } finally {
                setIsSubmitting(false)
            }
        },
        [activeCall, isSubmitting]
    )

    // Auto-submit after user stops entering digits
    useEffect(() => {
        if (!isVisible || !digits.trim() || isSubmitting || !activeCall) {
            if (autoSubmitTimerRef.current) {
                clearTimeout(autoSubmitTimerRef.current)
                autoSubmitTimerRef.current = null
            }
            return
        }

        // Clear existing timer
        if (autoSubmitTimerRef.current) {
            clearTimeout(autoSubmitTimerRef.current)
        }

        // Set new timer to auto-submit after delay
        autoSubmitTimerRef.current = setTimeout(() => {
            sendDigits(digits)
        }, AUTO_SUBMIT_DELAY)

        return () => {
            if (autoSubmitTimerRef.current) {
                clearTimeout(autoSubmitTimerRef.current)
                autoSubmitTimerRef.current = null
            }
        }
    }, [digits, isVisible, isSubmitting, activeCall, sendDigits])

    const handleNumberClick = (num: string) => {
        setDigits((prev) => prev + num)
    }

    const handleZeroPressStart = () => {
        isLongPressRef.current = false
        const timerId = setTimeout(() => {
            isLongPressRef.current = true
            setDigits((prev) => prev + "+")
            longPressTimerRef.current = null
        }, 500)
        longPressTimerRef.current = timerId
    }

    const handleZeroPressEnd = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current)
            longPressTimerRef.current = null
            if (!isLongPressRef.current) {
                setDigits((prev) => prev + "0")
            }
        }
        isLongPressRef.current = false
    }

    const keypadButtons = [
        {label: "1", value: "1", subLabel: ""},
        {label: "2", value: "2", subLabel: "ABC"},
        {label: "3", value: "3", subLabel: "DEF"},
        {label: "4", value: "4", subLabel: "GHI"},
        {label: "5", value: "5", subLabel: "JKL"},
        {label: "6", value: "6", subLabel: "MNO"},
        {label: "7", value: "7", subLabel: "PQRS"},
        {label: "8", value: "8", subLabel: "TUV"},
        {label: "9", value: "9", subLabel: "WXYZ"},
        {label: "*", value: "*", subLabel: ""},
        {label: "0", value: "0", subLabel: "+"},
        {label: "#", value: "#", subLabel: ""}
    ]

    return (
        <div className={`${styles.keypadGrid} ${!isVisible ? styles.hidden : ""}`}>
            {keypadButtons.map((button) => {
                if (button.value === "0") {
                    return (
                        <button
                            key={button.value}
                            className={styles.keypadButton}
                            onMouseDown={handleZeroPressStart}
                            onMouseUp={handleZeroPressEnd}
                            onMouseLeave={handleZeroPressEnd}
                            onTouchStart={handleZeroPressStart}
                            onTouchEnd={handleZeroPressEnd}
                            disabled={isSubmitting || !activeCall}
                            type="button">
                            <span className={styles.keypadButtonLabel}>{button.label}</span>
                            {button.subLabel && <span className={styles.keypadButtonSubLabel}>{button.subLabel}</span>}
                        </button>
                    )
                }
                return (
                    <button
                        key={button.value}
                        className={styles.keypadButton}
                        onClick={() => handleNumberClick(button.value)}
                        disabled={isSubmitting || !activeCall}
                        type="button">
                        <span className={styles.keypadButtonLabel}>{button.label}</span>
                        {button.subLabel && <span className={styles.keypadButtonSubLabel}>{button.subLabel}</span>}
                    </button>
                )
            })}
        </div>
    )
}
